// @flow

import * as Sentry from "@sentry/react";

// "Yeni Sipariş v2" ekranındaki (useCreateDeliveryRequest.js) reverse geocoding
// yaklaşımıyla aynı sağlayıcı: OpenStreetMap Nominatim, doğrudan tarayıcıdan.
// Bu dosya saf tutulur (React/store bağımsız); DB ile eşleştirme
// useSubCompanyLocationSync.js hook'unda yapılır.

const NOMINATIM_BASE = "https://nominatim.openstreetmap.org";
const REQUEST_TIMEOUT_MS = 8000;

// Nominatim'in Türkiye adres alanları bölgeye göre tutarsız isimlendiriliyor;
// en spesifikten en genele ilk dolu olanı alıyoruz.
const CITY_KEYS = ["province", "state", "city"];
const COUNTY_KEYS = ["county", "state_district", "town", "district", "municipality"];
const NEIGHBORHOOD_KEYS = ["suburb", "neighbourhood", "quarter", "city_district", "village"];

const pickFirst = (address, keys) => keys.map(key => address?.[key]).find(Boolean) || "";

// DB'deki mahalle adları "Caferağa Mah.", "Dadılar Köyü", "Göllü Mah (Gacık Köyü)" gibi
// ekli ve/veya eski köy adı parantezli formatta tutuluyor; Nominatim bu haliyle arama
// yaptığında çoğunlukla sonuç bulamıyor. "mah/mahalle(si)/köy(ü)" kelimesi görülen yerden
// itibaren (parantez içeriği dahil) her şey atılıp yalnızca çıplak ad bırakılıyor.
//
// Regex \b Türkçe'ye özgü harflerde (ı, ö, ğ, ş, ç, İ) kelime sınırını doğru
// tanımıyor — ör. "Kadıköy" öbek olarak "Kadı" + "köy"e bölünüp yanlışlıkla
// "Kadı"ya kırpılabiliyordu. Bunun yerine boşlukla ayrılmış TOKEN'lar üzerinde
// tam eşleşme aranıyor.
const NEIGHBORHOOD_SUFFIX_WORDS = new Set([
    "mahallesi",
    "mahalle",
    "mah",
    "mah.",
    "mh",
    "mh.",
    "köyü",
    "köy"
]);

const stripNeighborhoodSuffix = name => {
    if (!name) return "";
    const withoutParens = name.replace(/\(.*?\)/g, " ");
    const tokens = withoutParens.trim().split(/\s+/).filter(Boolean);
    const suffixIndex = tokens.findIndex(token =>
        NEIGHBORHOOD_SUFFIX_WORDS.has(token.toLocaleLowerCase("tr-TR"))
    );
    const kept = suffixIndex === -1 ? tokens : tokens.slice(0, suffixIndex);
    return kept.join(" ").trim();
};

// Nominatim: max 1 istek/sn politikası. Reverse + forward, iki form arasında
// paylaşılan tek sayaç üzerinden sıraya giriyor.
const MIN_INTERVAL_MS = 1100;
let lastRequestAt = 0;

const throttle = async () => {
    const wait = MIN_INTERVAL_MS - (Date.now() - lastRequestAt);
    if (wait > 0) {
        await new Promise(resolve => setTimeout(resolve, wait));
    }
    lastRequestAt = Date.now();
};

const fetchWithTimeout = async url => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
    try {
        return await fetch(url, {signal: controller.signal});
    } finally {
        clearTimeout(timeoutId);
    }
};

// Haritadan seçilen nokta -> il/ilçe/mahalle/adres (henüz DB ile eşleştirilmemiş, ham)
export const reverseGeocodeAddress = async ({lat, lng}) => {
    try {
        await throttle();
        const url =
            `${NOMINATIM_BASE}/reverse?format=jsonv2&lat=${lat}&lon=${lng}` +
            `&addressdetails=1&accept-language=tr&zoom=18`;
        const response = await fetchWithTimeout(url);
        if (!response.ok) return null;
        const data = await response.json();
        if (!data || data.error || !data.address) return null;

        return {
            city: pickFirst(data.address, CITY_KEYS),
            county: pickFirst(data.address, COUNTY_KEYS),
            neighborhood: pickFirst(data.address, NEIGHBORHOOD_KEYS),
            displayAddress: data.display_name || ""
        };
    } catch (error) {
        Sentry.captureException(error);
        return null;
    }
};

// İl/ilçe/mahalle -> koordinat
export const forwardGeocodeAddress = async ({city, county, neighborhood}) => {
    if (!city) return null;
    try {
        await throttle();
        const query = [stripNeighborhoodSuffix(neighborhood), county, city, "Türkiye"]
            .filter(Boolean)
            .join(", ");
        const url =
            `${NOMINATIM_BASE}/search?format=jsonv2&q=${encodeURIComponent(query)}` +
            `&countrycodes=tr&limit=1&accept-language=tr`;
        const response = await fetchWithTimeout(url);
        if (!response.ok) return null;
        const data = await response.json();
        if (!Array.isArray(data) || !data[0]) return null;

        return {
            latitude: Number(data[0].lat),
            longitude: Number(data[0].lon)
        };
    } catch (error) {
        Sentry.captureException(error);
        return null;
    }
};
